import React from 'react';
import BaseScadaNode from './BaseScadaNode';

export function SieveSVG({ config = {}, width = 80, height = 55, liveValues = {} }) {
    const running = parseFloat(liveValues.state ?? 1) >= parseFloat(config.threshold ?? 0.5);
    const hasError = liveValues.error === 1 || liveValues.error === true ||
        liveValues.error === '1' || liveValues.error === 'true';
    const statusColor = hasError ? '#EF4444' : running ? '#22C55E' : '#64748B';

    const bodyX = 4;
    const bodyY = 8;
    const bodyW = width - 8;
    const bodyH = height - 18;

    // Mesh lines (diagonal hatching inside body)
    const meshLines = [];
    const meshSpacing = 7;
    const meshColor = running ? '#4F46E5' : '#334155';
    for (let i = -bodyH; i < bodyW + bodyH; i += meshSpacing) {
        const x1 = Math.max(bodyX, bodyX + i);
        const y1 = i < 0 ? bodyY - i : bodyY;
        const x2 = Math.min(bodyX + bodyW, bodyX + i + bodyH);
        const y2 = i + bodyH > bodyW ? bodyY + bodyW + bodyH - i - bodyH : bodyY + bodyH;
        if (x1 < bodyX + bodyW && x2 > bodyX) {
            meshLines.push({ x1, y1, x2, y2 });
        }
    }

    // Vibration: animate transform when running
    const vibAnim = running ? (
        <animateTransform
            attributeName="transform"
            type="translate"
            values="0,0; 1.5,0; 0,0; -1.5,0; 0,0"
            dur="0.08s"
            repeatCount="indefinite"
        />
    ) : null;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Inlet stub at top */}
            <rect x={bodyX + bodyW / 2 - 4} y={0} width={8} height={bodyY} rx={1} fill="#475569" />

            {/* Main body group — vibrates when running */}
            <g>
                {vibAnim}

                {/* Body frame */}
                <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx={3}
                    fill="#1E293B" stroke={statusColor} strokeWidth={2} />

                {/* Mesh/screen lines */}
                <clipPath id={`sieveClip_${width}_${height}`}>
                    <rect x={bodyX + 1} y={bodyY + 1} width={bodyW - 2} height={bodyH - 2} />
                </clipPath>
                <g clipPath={`url(#sieveClip_${width}_${height})`}>
                    {meshLines.slice(0, 20).map((l, i) => (
                        <line key={i} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
                            stroke={meshColor} strokeWidth={1} opacity={0.7} />
                    ))}
                </g>

                {/* Status dot */}
                <circle cx={bodyX + 8} cy={bodyY + 8} r={4}
                    fill={statusColor} stroke="#0F172A" strokeWidth={1} />

                {/* Label */}
                <text x={bodyX + bodyW / 2} y={bodyY + bodyH / 2 + 4}
                    textAnchor="middle" fill="#64748B" fontSize={9} fontWeight="bold">TAMIZ</text>
            </g>

            {/* Accept outlet at bottom */}
            <rect x={bodyX + bodyW / 2 - 4} y={bodyY + bodyH} width={8} height={height - bodyY - bodyH - 2}
                rx={1} fill="#22C55E" opacity={0.6} />

            {/* Reject outlet on right side */}
            <rect x={bodyX + bodyW} y={bodyY + bodyH / 2 - 3} width={width - bodyX - bodyW} height={6}
                rx={1} fill="#EF4444" opacity={0.5} />

            {/* Error flash */}
            {hasError && (
                <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx={3}
                    fill="none" stroke="#EF4444" strokeWidth={3}>
                    <animate attributeName="stroke-opacity" values="1;0;1" dur="0.6s" repeatCount="indefinite" />
                </rect>
            )}
        </svg>
    );
}

export default function SieveNode(props) {
    const { data } = props;
    const w = data?.width || 80;
    const h = data?.height || 55;
    return (
        <BaseScadaNode {...props}>
            <SieveSVG config={data?.config} width={w} height={h} liveValues={data?.liveValues || {}} />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', marginTop: 2,
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
